import type { KeyboardEvent, ReactNode } from 'react';

export type HelperEmeraldSegmentTabItem<T extends string> = {
  key: T;
  label: string;
  badgeCount?: number;
};

type HelperEmeraldSegmentTabBarProps<T extends string> = {
  tabs: HelperEmeraldSegmentTabItem<T>[];
  activeKey: T;
  onChange: (key: T) => void;
  ariaLabel: string;
  className?: string;
  /** Slightly tighter tab padding/typography when sharing a row with trailing controls. */
  compact?: boolean;
};

const renderBadge = (count: number, isActive: boolean): ReactNode => {
  if (count <= 0) {
    return null;
  }
  return (
    <span
      className={`ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold ${
        isActive ? 'bg-red-400 text-white' : 'bg-red-500 text-white'
      }`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

/** Segment tab bar — filename kept for imports; colors use brand `primary` (style guide §5.5). */
export const HelperEmeraldSegmentTabBar = <T extends string>({
  tabs,
  activeKey,
  onChange,
  ariaLabel,
  className = '',
  compact = false,
}: HelperEmeraldSegmentTabBarProps<T>) => {
  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, key: T) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onChange(key);
    }
  };

  return (
    <div
      className={`flex rounded-full border border-primary-600 bg-white p-1 dark:border-primary-500 dark:bg-gray-900/90 ${className}`}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab) => {
        const isActive = activeKey === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={0}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => handleTabKeyDown(event, tab.key)}
            className={`flex min-h-10 flex-1 items-center justify-center gap-1 rounded-full font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              compact ? 'px-1 text-xs' : 'px-1.5 text-sm'
            } ${
              isActive
                ? 'bg-primary-700 font-semibold text-white shadow-sm dark:bg-primary-600'
                : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
            {tab.badgeCount !== undefined ? renderBadge(tab.badgeCount, isActive) : null}
          </button>
        );
      })}
    </div>
  );
};
